import { Outlet, NavLink, useNavigate, useLocation, Link } from 'react-router-dom'
import {
  LayoutDashboard,
  User,
  LogOut,
  Menu,
  X,
  Settings,
  Star,
  HelpCircle,
  Car,
  FileText,
  CalendarCheck,
  ExternalLink,
  BookOpen,
  AlertTriangle,
  ClipboardList,
  CheckCircle2,
  EyeOff,
  Sparkles,
} from 'lucide-react'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { cn } from '@/lib/utils'
import { getPublicSiteUrl } from '@/lib/domain'
import { ImpersonationBanner } from '@/components/ImpersonationBanner'
import { useOnboardingData } from '@/hooks/useOnboardingData'
import { evaluateOnboarding } from '@/lib/onboarding'
import { OnboardingProgressDialog } from '@/components/accounts/OnboardingProgressDialog'
import { Progress } from '@/components/ui/progress'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { supabase } from '@/lib/supabase'

// Stripe settings keys
const STRIPE_SETTINGS = {
  ENABLED: 'stripe_enabled',
  PUBLISHABLE_KEY: 'stripe_publishable_key',
  SECRET_KEY: 'stripe_secret_key',
  MODE: 'stripe_mode',
  PAYMENT_MODE: 'stripe_payment_mode',
  CONNECT_ACCOUNT_ID: 'stripe_connect_account_id',
} as const

/**
 * How this account's payments are processed. Set by the Connect flow, not by the
 * operator: 'connect' accounts run on platform credentials plus their connected
 * account, so they never enter their own API keys.
 */
type StripePaymentMode = 'keys' | 'connect' | 'disconnected'

/**
 * A nav entry. `children` renders an indented sub-list under the item — used by
 * Vehicles to split the fleet into Active and Inactive, each a real route.
 * `countKey` picks which tally from `vehicleCounts` to show on the right.
 */
interface NavItem {
  name: string
  href: string
  icon: typeof LayoutDashboard
  end?: boolean
  countKey?: 'activeVehicles' | 'inactiveVehicles'
  children?: NavItem[]
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Bookings', href: '/dashboard/bookings', icon: CalendarCheck },
  { name: 'Quotes', href: '/dashboard/quotes', icon: ClipboardList },
  { name: 'Reviews', href: '/dashboard/reviews', icon: Star },
  { name: 'FAQs', href: '/dashboard/faqs', icon: HelpCircle },
  {
    name: 'Vehicles',
    href: '/dashboard/vehicles',
    icon: Car,
    // `end` so the parent doesn't stay highlighted while a child is selected.
    end: true,
    children: [
      {
        name: 'Active',
        href: '/dashboard/vehicles/active',
        icon: CheckCircle2,
        countKey: 'activeVehicles',
      },
      {
        name: 'Inactive',
        href: '/dashboard/vehicles/inactive',
        icon: EyeOff,
        countKey: 'inactiveVehicles',
      },
    ],
  },
  { name: 'Pages', href: '/dashboard/pages', icon: FileText },
  { name: 'Otto AI', href: '/dashboard/otto-ai', icon: Sparkles },
]

/**
 * True when one of this item's sub-items owns the current route, so the parent
 * can stand down. Compared on the full path (not just the suffix) so an item
 * whose child paths differ only by prefix can't be confused for another's.
 */
function isChildRouteActive(item: NavItem, pathname: string): boolean {
  return Boolean(item.children?.some((child) => pathname === child.href))
}

export function UserLayout() {
  const { signOut, profile, account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [onboardingOpen, setOnboardingOpen] = useState(false)

  // Check if on docs page - docs has its own sidebar that replaces the main one
  const isDocsPage = location.pathname.includes('/docs')

  // Use impersonated account when impersonating, otherwise use real account
  const displayAccount = isImpersonating ? impersonatedAccount : account

  // Onboarding progress (for admin impersonation view)
  const { data: onboardingData } = useOnboardingData(isImpersonating && !!displayAccount)
  const onboardingResult = displayAccount && onboardingData
    ? evaluateOnboarding(displayAccount, onboardingData)
    : null

  // Query Stripe configuration status
  const { data: stripeStatus } = useQuery({
    queryKey: ['stripe-status', displayAccount?.id],
    queryFn: async () => {
      if (!displayAccount?.id) return { isConfigured: true } // Hide warning if no account

      const { data, error } = await supabase
        .from('account_settings')
        .select('key, value')
        .eq('account_id', displayAccount.id)
        .in('key', [
          STRIPE_SETTINGS.ENABLED,
          STRIPE_SETTINGS.PUBLISHABLE_KEY,
          STRIPE_SETTINGS.SECRET_KEY,
          STRIPE_SETTINGS.MODE,
          STRIPE_SETTINGS.PAYMENT_MODE,
          STRIPE_SETTINGS.CONNECT_ACCOUNT_ID,
        ])

      if (error) {
        console.error('Error fetching Stripe status:', error)
        return { isConfigured: true } // Hide warning on error
      }

      const settings: Record<string, string> = {}
      data?.forEach((s) => {
        if (s.value) settings[s.key] = s.value
      })

      const isEnabled = settings[STRIPE_SETTINGS.ENABLED] === 'true'
      const hasPublishableKey = Boolean(settings[STRIPE_SETTINGS.PUBLISHABLE_KEY])
      const hasSecretKey = Boolean(settings[STRIPE_SETTINGS.SECRET_KEY])
      const paymentMode =
        (settings[STRIPE_SETTINGS.PAYMENT_MODE] as StripePaymentMode) || 'keys'
      const hasConnectAccount = Boolean(settings[STRIPE_SETTINGS.CONNECT_ACCOUNT_ID])
      const isTestMode = settings[STRIPE_SETTINGS.MODE] === 'test'

      // Which credentials an account actually needs depends on how it pays.
      // Requiring the account's own keys unconditionally reported every
      // Connect and test-mode account as unconfigured, because neither ever
      // supplies them. Mirrors the server-side rules in the viewer's
      // isStripeConfigured() and the validation on the Integrations page.
      let isConfigured: boolean
      if (paymentMode === 'connect') {
        // Platform credentials + the connected account id; no account keys.
        isConfigured = isEnabled && hasConnectAccount
      } else if (paymentMode === 'disconnected') {
        // Was connected, then the client revoked access — payments really are
        // down, so this account SHOULD be warned.
        isConfigured = false
      } else if (isTestMode) {
        // Test mode runs on the platform's test credentials. Those live in
        // platform_settings, which this (account-scoped) query can't see, so
        // `enabled` is as far as the check can go here.
        isConfigured = isEnabled
      } else {
        isConfigured = isEnabled && hasPublishableKey && hasSecretKey
      }

      return {
        isConfigured,
        isEnabled,
        hasPublishableKey,
        hasSecretKey,
        paymentMode,
        hasConnectAccount,
        isTestMode,
      }
    },
    enabled: Boolean(displayAccount?.id),
    staleTime: 60000, // Cache for 1 minute
  })

  // Fleet tallies for the Vehicles sub-items. Only `is_active` is selected, so
  // this stays cheap enough to run on every Builder page.
  const { data: vehicleCounts } = useQuery({
    queryKey: ['vehicle-status-counts', displayAccount?.id],
    queryFn: async () => {
      if (!displayAccount?.id) return { activeVehicles: 0, inactiveVehicles: 0 }
      const { data, error } = await supabase
        .from('vehicles')
        .select('is_active')
        .eq('account_id', displayAccount.id)
      if (error) throw error
      return {
        activeVehicles: data.filter((v) => v.is_active).length,
        inactiveVehicles: data.filter((v) => !v.is_active).length,
      }
    },
    enabled: Boolean(displayAccount?.id),
    staleTime: 60000,
  })

  const showStripeWarning = stripeStatus && !stripeStatus.isConfigured

  const handleSignOut = async () => {
    await signOut()
    navigate('/login')
  }

  // Build base path for navigation - different when impersonating
  const basePath = isImpersonating
    ? `/admin/accounts/${impersonatedAccount?.slug}/view`
    : ''

  const mapNavItem = (item: NavItem): NavItem => {
    if (!isImpersonating) return item
    // Convert /dashboard to /view/dashboard, /dashboard/reviews to /view/reviews
    const path = item.href === '/dashboard'
      ? '/dashboard'
      : item.href.replace('/dashboard/', '/')
    return {
      ...item,
      href: `${basePath}${path}`,
      // Sub-items need the same rewrite, or the Vehicles children would link out
      // of the impersonation view and drop the admin back to their own account.
      ...(item.children ? { children: item.children.map(mapNavItem) } : {}),
    }
  }

  const navItems = navigation.map(mapNavItem)

  return (
    <div className="min-h-screen bg-muted/30">
      {isImpersonating && <ImpersonationBanner />}

      {/* Mobile sidebar backdrop - hidden on docs pages */}
      {sidebarOpen && !isDocsPage && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - hidden on docs pages (docs has its own sidebar) */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-64 transform flex-col bg-card shadow-lg transition-transform duration-200 ease-in-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          isImpersonating && 'top-12',
          isDocsPage && 'hidden'
        )}
      >
        <div className="border-b px-4">
          <div className="flex h-16 items-center justify-between">
            <div>
              <h1 className="text-xl font-bold">
                {displayAccount?.name || 'Dashboard'}
              </h1>
              {displayAccount?.slug && (
                <p className="text-xs text-muted-foreground">
                  {displayAccount.slug}
                </p>
              )}
            </div>
            <button
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          {isImpersonating && onboardingResult && (
            <button
              onClick={() => setOnboardingOpen(true)}
              className="flex w-full items-center gap-2 pb-3 cursor-pointer group"
              title={`${onboardingResult.completedSteps.length}/${onboardingResult.totalSteps} onboarding steps complete`}
            >
              <Progress
                value={onboardingResult.percentage}
                className={cn(
                  'h-1.5 flex-1',
                  onboardingResult.percentage === 100
                    ? '[&>div]:bg-green-500'
                    : onboardingResult.percentage >= 60
                      ? '[&>div]:bg-blue-500'
                      : onboardingResult.percentage >= 30
                        ? '[&>div]:bg-amber-500'
                        : '[&>div]:bg-red-500'
                )}
              />
              <span className={cn(
                'text-xs font-medium tabular-nums group-hover:underline',
                onboardingResult.percentage === 100 ? 'text-green-600' : 'text-muted-foreground'
              )}>
                {onboardingResult.percentage}%
              </span>
            </button>
          )}
        </div>

        {/* flex-1 + min-h-0 (not a hard-coded calc) so the nav always fits below
            the header, whose height varies with the onboarding progress bar —
            the old calc(100%-4rem) clipped Sign out when the bar was shown. */}
        <nav className="flex min-h-0 flex-1 flex-col p-4 overflow-y-auto">
          <ul className="flex-1 space-y-1">
            {navItems.map((item) => (
              <li key={item.name}>
                <NavLink
                  to={item.href}
                  end={
                    item.href === '/dashboard' ||
                    item.href.endsWith('/view/dashboard')
                  }
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      // A child route (…/vehicles/active) also matches the parent
                      // prefix, so the parent yields the highlight to it rather
                      // than lighting both rows at once.
                      isActive && !isChildRouteActive(item, location.pathname)
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    )
                  }
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </NavLink>

                {item.children && item.children.length > 0 && (
                  <ul className="mt-1 space-y-1 border-l pl-3 ml-4">
                    {item.children.map((child) => (
                      <li key={child.name}>
                        <NavLink
                          to={child.href}
                          className={({ isActive }) =>
                            cn(
                              'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
                              isActive
                                ? 'bg-primary/10 font-medium text-primary'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                            )
                          }
                        >
                          <child.icon className="h-4 w-4" />
                          <span className="flex-1">{child.name}</span>
                          {child.countKey && vehicleCounts && (
                            <span className="tabular-nums text-xs text-muted-foreground">
                              {vehicleCounts[child.countKey]}
                            </span>
                          )}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>

          <div className="mt-auto border-t pt-4 space-y-1">
            <NavLink
              to={isImpersonating ? `${basePath}/docs` : '/dashboard/docs'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )
              }
            >
              <BookOpen className="h-5 w-5" />
              Help
            </NavLink>
            {displayAccount?.slug && (
              <button
                onClick={() => {
                  const url = displayAccount.custom_domain
                    ? `https://${displayAccount.custom_domain}`
                    : getPublicSiteUrl(displayAccount.slug)
                  window.open(url, '_blank')
                }}
                className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <ExternalLink className="h-5 w-5" />
                View Website
              </button>
            )}
            <NavLink
              to={isImpersonating ? `${basePath}/settings` : '/dashboard/settings'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )
              }
            >
              <div className="relative">
                <Settings className="h-5 w-5" />
                {showStripeWarning && (
                  <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-amber-500" />
                )}
              </div>
              Settings
            </NavLink>
            <button
              onClick={handleSignOut}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <LogOut className="h-5 w-5" />
              Sign out
            </button>
          </div>
        </nav>
      </aside>

      {/* Main content */}
      <div className={cn(
        isDocsPage ? '' : 'lg:pl-64',
        isImpersonating && 'pt-12'
      )}>
        {/* Mobile header - hidden on docs pages (docs has its own) */}
        {!isDocsPage && (
          <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b bg-card px-4 lg:hidden">
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar"
            >
              <Menu className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-semibold">
              {displayAccount?.name || 'Dashboard'}
            </h1>
          </header>
        )}

        {/* Desktop header - hidden on docs pages */}
        {!isImpersonating && !isDocsPage && (
          <header className="sticky top-0 z-30 hidden h-16 items-center justify-end border-b bg-card px-6 lg:flex">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted focus:outline-none">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="text-sm">
                    {(profile?.full_name || profile?.email || 'U')
                      .charAt(0)
                      .toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium">
                  {profile?.full_name || profile?.email}
                </span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate('/dashboard/profile')}
                  className="cursor-pointer"
                >
                  <User className="mr-2 h-4 w-4" />
                  User Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/dashboard/settings')}
                  className="cursor-pointer"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleSignOut}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>
        )}

        <main className={cn(isDocsPage ? '' : 'p-4 lg:p-8')}>
          {/* Stripe Configuration Warning */}
          {showStripeWarning && !isDocsPage && (
            <Alert variant="destructive" className="mb-6 border-amber-300 bg-amber-50 text-amber-900">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between">
                <span>
                  <strong>Payment Processing Not Configured</strong> - Customers cannot complete bookings online.{' '}
                  <Link
                    to={isImpersonating ? `${basePath}/settings/integrations` : '/dashboard/settings/integrations'}
                    className="underline font-medium hover:text-amber-800"
                  >
                    Configure Stripe
                  </Link>
                </span>
              </AlertDescription>
            </Alert>
          )}
          <Outlet />
        </main>
      </div>

      {isImpersonating && displayAccount && (
        <OnboardingProgressDialog
          account={displayAccount}
          data={onboardingData}
          open={onboardingOpen}
          onOpenChange={setOnboardingOpen}
        />
      )}
    </div>
  )
}
